"use client"

import * as React from "react"

import { cn } from "@workspace/ui/lib/utils"

/**
 * An announcement's video. Ambient by default — muted, looped, no controls —
 * and playing only while on screen. Under `prefers-reduced-motion` or a
 * Save-Data connection it never starts by itself and waits on its first frame:
 * as side media it then shows native controls so the diner can choose to play
 * it; as a `decorative` background it stays still (and hidden from assistive
 * tech) — a background is never something to operate.
 */
export function AnnouncementVideo({
  src,
  label,
  decorative = false,
  className,
}: {
  src: string
  /** Accessible name (the media caption or the announcement title). */
  label?: string
  /** A panel background: no controls ever, `aria-hidden`. */
  decorative?: boolean
  className?: string
}) {
  const ref = React.useRef<HTMLVideoElement>(null)
  const [ambient, setAmbient] = React.useState(false)

  React.useEffect(() => {
    const video = ref.current
    if (!video) return
    const motion = window.matchMedia("(prefers-reduced-motion: no-preference)")
    const saveData =
      (navigator as Navigator & { connection?: { saveData?: boolean } })
        .connection?.saveData === true
    let visible = false

    // Off-screen or with motion off, an ambient loop pauses; playback the
    // diner started from the controls is left alone while scrolling.
    const sync = () => {
      const play = motion.matches && !saveData
      setAmbient(play)
      if (play && visible) video.play().catch(() => {})
      else if (play) video.pause()
    }
    const onMotionChange = () => {
      if (!motion.matches) video.pause()
      sync()
    }
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? false
      sync()
    })
    observer.observe(video)
    motion.addEventListener("change", onMotionChange)
    return () => {
      observer.disconnect()
      motion.removeEventListener("change", onMotionChange)
    }
  }, [])

  return (
    <video
      ref={ref}
      src={src}
      muted
      loop
      playsInline
      preload="metadata"
      controls={!decorative && !ambient}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : label}
      tabIndex={decorative ? -1 : undefined}
      className={cn("size-full object-cover", className)}
    />
  )
}
